import { getApiBaseUrl, getMerchantClient } from '@beatq/sdk-client';
import {
  ApiError,
  csrfHeaders,
  getAccessToken,
  isIdentitySessionError,
  mapErrorCode,
  readEntityFromEnvelope,
  readListFromEnvelope,
  readObjectFieldFromEnvelope,
  beatqDelIdentity,
} from '@beatq/shared';
import { emptyListPayload, isOptionalReadMiss } from '../../shell/merchantApiSoftFail.js';
import { merchantScopeHeaders } from '../../shell/merchantContext.js';
import { MERCHANT_HISTORY_LIMIT, MERCHANT_HISTORY_STATUSES } from './catalog.js';

type ApiErr = { error?: { code: string; message: string } };

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  if (e?.error && isIdentitySessionError(e.error.code)) {
    throw new ApiError(e.error.code, e.error.message);
  }
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

/** ASM-152 merchant acceptance window. */
export const ACCEPT_WINDOW_MS = 180_000;
export const ACCEPT_ESCALATE_MS = 30_000;
export const BOARD_SORT_PIN_MS = 3_000;

export type OrderSummary = {
  id?: string;
  branchId?: string | null;
  publicReference?: string | null;
  reference?: string | null;
  status?: string;
  fulfilmentMode?: string | null;
  promisedAt?: string | null;
  dueAt?: string | null;
  placedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  handedOverAt?: string | null;
  grandTotal?: { minor?: number | null; currency?: string | null } | null;
  /** Server accept window; used for incoming countdown (SCR-MER-050/051). */
  acceptanceDeadlineAt?: string | null;
  acceptanceDeadline?: string | null;
  secondsToDeadline?: number | null;
  customerName?: string | null;
  customerFirstName?: string | null;
  itemCount?: number | null;
  itemSummary?: string | null;
  totalMinor?: number | null;
  total?: { minor?: number | null; currency?: string | null } | null;
  claimedByName?: string | null;
  acceptedByName?: string | null;
  collectionCode?: string | null;
  requiresAgeVerification?: boolean | null;
  hasAllergenNote?: boolean | null;
  isScheduled?: boolean | null;
  scheduledSlot?: { startAt?: string | null; label?: string | null } | null;
  isCourierWaiting?: boolean | null;
  delivery?: {
    status?: string | null;
    courierFirstName?: string | null;
    courierEtaAt?: string | null;
    isCourierWaiting?: boolean | null;
  } | null;
  printFailed?: boolean | null;
};

export type OrderLine = {
  id?: string;
  name?: string;
  productName?: string;
  variantName?: string;
  quantity?: number | null;
  lineTotal?: { minor?: number | null; currency?: string | null } | null;
  modifiers?: { name?: string; priceDelta?: number | null }[];
  allergens?: string[] | null;
  notes?: string | null;
  isAgeRestricted?: boolean | null;
  requiresRefrigeration?: boolean | null;
  isPerishable?: boolean | null;
  isChilled?: boolean | null;
  stockItemId?: string | null;
  productId?: string | null;
  isUnavailable?: boolean | null;
  outOfStock?: boolean | null;
  unavailable?: boolean | null;
  availabilityStatus?: string | null;
  stockStatus?: string | null;
};

export type OrderMoney = {
  minor: number;
  currency: string;
};

export type MerchantSettlement = {
  originalMerchandiseSubtotal: OrderMoney;
  platformFundedDiscount: OrderMoney;
  commissionRateBps: number | null;
  commission: OrderMoney;
  includedMerchandiseVat?: OrderMoney;
  merchantEntitlement: OrderMoney;
  pricingPolicy: { id: string; version: number } | null;
};

export type OrderPriceBreakdown = {
  /** Merchandise subtotal after the customer discount. */
  subtotal: OrderMoney;
  originalSubtotal: OrderMoney;
  discount: OrderMoney;
  deliveryFee: OrderMoney;
  adminFee?: OrderMoney;
  /** Legacy alias of adminFee. */
  serviceFee?: OrderMoney;
  /** Inclusive VAT; accounting information only. */
  tax: OrderMoney;
  tip: OrderMoney;
  total: OrderMoney;
  delivery: {
    brand: 'BeatQ Del';
    roadDistanceMetres: number;
  };
};

export type OrderDetail = OrderSummary & {
  branchId?: string | null;
  notes?: string | null;
  grandTotal?: { minor?: number | null; currency?: string | null } | null;
  merchantSettlement?: MerchantSettlement | null;
  priceBreakdown?: OrderPriceBreakdown | null;
  lines?: OrderLine[];
  timeline?: OrderTimelineEvent[];
  customer?: {
    firstName?: string | null;
    fullName?: string | null;
    isRepeatCustomer?: boolean | null;
    deliveryAddress?: unknown;
  } | null;
  deliveryAddress?: unknown;
  address?: unknown;
  returnReason?: string | null;
  failureReason?: string | null;
  handoverFailureReason?: string | null;
  liabilityPosition?: string | null;
  qualifyingDateOfBirth?: string | null;
  qualifyingDateOfBirthLabel?: string | null;
  statutoryMinimumAge?: number | null;
  restrictedLines?: Array<{ lineId?: string; productName?: string; minimumAge?: number }>;
};

export type OrderTimelineEvent = {
  id?: string;
  label?: string;
  occurredAt?: string | null;
  actor?: string | null;
  type?: string | null;
  message?: string | null;
  status?: string | null;
};

export type DeliveryStatus = {
  /** Legacy / alias of stage */
  state?: string;
  stage?: string;
  stageLabel?: string;
  etaMinutes?: number | null;
  etaAt?: string | null;
  courierName?: string | null;
  courierPresence?: string | null;
  lastUpdatedAt?: string | null;
  issueId?: string | null;
  issueStatus?: string | null;
};

export function formatCourierPresence(value?: string | null): string | null {
  if (!value?.trim()) return null;
  const known: Record<string, string | null> = {
    NONE: null,
    EN_ROUTE_TO_BRANCH: 'Heading to the restaurant',
    AT_BRANCH: 'At the restaurant',
    AT_PICKUP: 'At the pickup',
    EN_ROUTE_TO_CUSTOMER: 'Heading to the customer',
    AT_CUSTOMER: 'At the customer',
    AT_DROPOFF: 'At the drop-off',
    RETURNING: 'Returning to the restaurant',
    RETURNED_TO_ORIGIN: 'Returned to the restaurant',
  };
  const normalized = value.trim().toUpperCase();
  if (normalized in known) return known[normalized] ?? null;
  return value
    .trim()
    .toLowerCase()
    .split(/[_\s]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export type DeliveryDispatchState = 'available' | 'pending' | 'assigned' | 'retry';

export function deliveryDispatchState(delivery?: DeliveryStatus): DeliveryDispatchState {
  const stage = (delivery?.stage ?? delivery?.state ?? '').toUpperCase();
  if (!stage || stage.includes('AWAITING_DISPATCH') || stage.includes('SETUP_PENDING')) {
    return 'available';
  }
  if (stage.includes('FINDING_RIDER') || stage.includes('DISPATCH_REQUESTED')) {
    return 'pending';
  }
  if (stage.includes('FAILED') || stage.includes('CANCELLED') || stage.includes('ENDED')) {
    return 'retry';
  }
  return isDeliveryAlreadyDispatched(delivery) ? 'assigned' : 'available';
}

export function deliveryRiderStatusLabel(delivery?: DeliveryStatus): string {
  const state = deliveryDispatchState(delivery);
  if (state === 'available' || state === 'pending') return 'Not assigned yet';
  const branded = beatqRiderLabel(delivery?.courierName);
  if (branded === 'BeatQ Del') return 'Rider assigned';
  return branded.replace(/^BeatQ Del\s*·\s*/, '');
}

export function deliveryJourneyLabel(delivery?: DeliveryStatus): string | null {
  if (deliveryDispatchState(delivery) === 'pending') return null;
  return formatCourierPresence(delivery?.courierPresence);
}

export function deliveryStageTone(
  delivery?: Pick<DeliveryStatus, 'stage' | 'state'> | null,
): 'prep' | 'out' | 'ready' {
  const stage = (delivery?.stage ?? delivery?.state ?? '').toUpperCase();
  if (
    stage.includes('DELIVERED') ||
    stage.includes('COLLECTED') ||
    stage.includes('COMPLETE') ||
    stage.includes('RETURNED_TO_ORIGIN')
  ) {
    return 'ready';
  }
  if (
    stage.includes('EN_ROUTE') ||
    stage.includes('ARRIVED') ||
    stage.includes('PICKED_UP') ||
    stage.includes('IN_TRANSIT') ||
    stage.includes('AT_PICKUP') ||
    stage.includes('AT_DROPOFF') ||
    stage.includes('RETURNING')
  ) {
    return 'out';
  }
  return 'prep';
}

function claimNameFromOrder(order: OrderSummary): string | null {
  const raw = order as OrderSummary & {
    claimedBy?: { firstName?: string; name?: string } | string | null;
    acceptedBy?: { firstName?: string; name?: string } | string | null;
    staffAttribution?: { acceptedByName?: string; acceptedBy?: string } | null;
  };
  const fromObj = (value: unknown): string | null => {
    if (!value) return null;
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'object') {
      const rec = value as { firstName?: string; name?: string };
      return rec.firstName?.trim() || rec.name?.trim() || null;
    }
    return null;
  };
  return (
    order.claimedByName?.trim() ||
    order.acceptedByName?.trim() ||
    fromObj(raw.claimedBy) ||
    fromObj(raw.acceptedBy) ||
    raw.staffAttribution?.acceptedByName?.trim() ||
    raw.staffAttribution?.acceptedBy?.trim() ||
    null
  );
}

function firstNameOnly(name?: string | null): string | null {
  if (!name?.trim()) return null;
  return name.trim().split(/\s+/)[0] ?? null;
}

function flattenBoardColumnOrders(envelope: unknown): OrderSummary[] | null {
  if (!envelope || typeof envelope !== 'object') return null;
  const root = envelope as Record<string, unknown>;
  const nested =
    root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root;
  const columns = nested.columns;
  if (!Array.isArray(columns) || columns.length === 0) return null;
  const orders: OrderSummary[] = [];
  for (const col of columns) {
    if (!col || typeof col !== 'object') continue;
    const list = (col as { orders?: unknown }).orders;
    if (Array.isArray(list)) {
      for (const row of list) {
        if (row && typeof row === 'object') orders.push(row as OrderSummary);
      }
    }
  }
  return orders.length ? orders : [];
}

export function boardGeneratedAtFromEnvelope(envelope: unknown): string | null {
  if (!envelope || typeof envelope !== 'object') return null;
  const root = envelope as Record<string, unknown>;
  const nested =
    root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root;
  const generatedAt = nested.generatedAt;
  return typeof generatedAt === 'string' ? generatedAt : null;
}

/** Offset to add to Date.now() to approximate server time. */
export function serverClockOffsetMs(generatedAt?: string | null, receivedAt = Date.now()): number {
  if (!generatedAt) return 0;
  const t = Date.parse(generatedAt);
  return Number.isFinite(t) ? t - receivedAt : 0;
}

function normalizeOrderSummary(order: OrderSummary): OrderSummary {
  const deadline = order.acceptanceDeadlineAt ?? order.acceptanceDeadline ?? null;
  const totalMinor = order.totalMinor ?? order.total?.minor ?? order.grandTotal?.minor ?? null;
  const first = order.customerFirstName?.trim() || firstNameOnly(order.customerName) || null;
  const promisedAt = order.promisedAt ?? order.dueAt ?? null;
  return {
    ...order,
    publicReference: order.publicReference ?? order.reference ?? null,
    acceptanceDeadlineAt: deadline,
    promisedAt,
    totalMinor,
    customerFirstName: first,
    customerName: first ?? order.customerName ?? null,
    claimedByName: claimNameFromOrder(order),
    isCourierWaiting: order.isCourierWaiting ?? order.delivery?.isCourierWaiting ?? null,
    isScheduled: Boolean(order.isScheduled || order.scheduledSlot),
  };
}

function normalizeOrderLine(line: OrderLine): OrderLine {
  const name = line.name ?? line.productName ?? 'Item';
  return {
    ...line,
    name,
    productName: line.productName ?? name,
  };
}

function normalizeOrderDetail(order: OrderDetail): OrderDetail {
  return {
    ...normalizeOrderSummary(order),
    lines: order.lines?.map(normalizeOrderLine),
  };
}

/** Human-readable line label (API may send productName / variantName instead of name). */
export function lineDisplayName(line: OrderLine): string {
  const base = line.name ?? line.productName ?? 'Item';
  if (line.variantName && line.variantName !== base) {
    return `${base} (${line.variantName})`;
  }
  return base;
}

function titleCaseEnum(value: string): string {
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatOrderStatus(status?: string | null): string {
  if (!status) return 'Unknown';
  return titleCaseEnum(status);
}

export function formatFulfilmentMode(mode?: string | null): string {
  if (!mode) return '—';
  return titleCaseEnum(mode);
}

export function isMerchantHistoryStatus(status?: string | null): boolean {
  if (!status) return false;
  return (MERCHANT_HISTORY_STATUSES as readonly string[]).includes(status.toUpperCase());
}

export function historyStatusBadgeTone(
  status?: string | null,
): 'success' | 'danger' | 'warning' | 'neutral' {
  const normalized = (status ?? '').toUpperCase();
  if (['COMPLETED', 'COLLECTED', 'DELIVERED'].includes(normalized)) return 'success';
  if (['PAYMENT_FAILED', 'DELIVERY_FAILED', 'REJECTED', 'CANCELLED'].includes(normalized)) {
    return 'danger';
  }
  if (normalized === 'EXPIRED') return 'warning';
  return 'neutral';
}

export function historyOccurredAt(order: OrderSummary): string | null {
  return order.completedAt ?? order.cancelledAt ?? order.handedOverAt ?? order.placedAt ?? null;
}

export function formatMerchantDateTime(iso?: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Europe/London',
  }).format(new Date(t));
}

export function formatHistoryOccurredAt(order: OrderSummary): string | null {
  return formatMerchantDateTime(historyOccurredAt(order));
}

export function paginateMerchantHistory<T>(
  rows: T[],
  page: number,
  pageSize: number,
): {
  page: number;
  pageCount: number;
  slice: T[];
  from: number;
  to: number;
  total: number;
} {
  const total = rows.length;
  const size = Math.max(1, pageSize);
  const pageCount = Math.max(1, Math.ceil(total / size) || 1);
  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = total === 0 ? 0 : (safePage - 1) * size;
  const slice = rows.slice(start, start + size);
  return {
    page: safePage,
    pageCount,
    slice,
    from: total === 0 ? 0 : start + 1,
    to: start + slice.length,
    total,
  };
}

export function filterMerchantHistoryOrders(
  orders: OrderSummary[],
  opts: { status?: string; query?: string } = {},
): OrderSummary[] {
  const status = opts.status?.trim().toUpperCase();
  const q = opts.query?.trim().toLowerCase();
  return orders.filter((order) => {
    if (status && (order.status ?? '').toUpperCase() !== status) return false;
    if (q) {
      const hay =
        `${order.publicReference ?? ''} ${order.reference ?? ''} ${order.id ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function historyEmptyMessage(opts: { query?: string; status?: string }): string {
  if (opts.query?.trim()) return 'No orders match that reference.';
  if (opts.status?.trim()) return 'No orders in this period.';
  return 'No orders in this period.';
}

export function buildOrderHistoryExportPayload(params?: { status?: string; branchId?: string }): {
  reportType: 'operations';
  format: 'CSV';
  parameters: Record<string, string>;
} {
  const parameters: Record<string, string> = {};
  if (params?.status) parameters.status = params.status;
  if (params?.branchId) parameters.branchId = params.branchId;
  return { reportType: 'operations', format: 'CSV', parameters };
}

export { MERCHANT_HISTORY_LIMIT, MERCHANT_HISTORY_STATUSES };

export function ordersFromEnvelope(envelope: unknown): OrderSummary[] {
  const fromColumns = flattenBoardColumnOrders(envelope);
  if (fromColumns) {
    return fromColumns.map(normalizeOrderSummary);
  }
  return readListFromEnvelope<OrderSummary>(envelope, ['orders', 'board', 'data']).map(
    normalizeOrderSummary,
  );
}

export function orderFromEnvelope(envelope: unknown): OrderDetail | undefined {
  const nested = readObjectFieldFromEnvelope<OrderDetail>(envelope, ['order']);
  if (nested && typeof nested === 'object' && ('id' in nested || 'reference' in nested)) {
    return normalizeOrderDetail(nested);
  }
  const data = readEntityFromEnvelope<OrderDetail>(envelope, ['order']);
  if (
    data &&
    typeof data === 'object' &&
    ('id' in data || 'reference' in data || 'status' in data)
  ) {
    return normalizeOrderDetail(data);
  }
  return undefined;
}

export function timelineFromEnvelope(envelope: unknown): OrderTimelineEvent[] {
  const order = orderFromEnvelope(envelope);
  if (order?.timeline) {
    return order.timeline.map((event) => ({
      ...event,
      label: formatTimelineLabel(event),
    }));
  }
  const raw = readListFromEnvelope<OrderTimelineEvent & { message?: string; type?: string }>(
    envelope,
    ['timeline', 'events'],
  );
  return raw.map((event) => ({
    ...event,
    label: formatTimelineLabel(event),
    actor: event.actor?.trim() || null,
  }));
}

export function formatTimelineActor(actor?: string | null): string | null {
  const trimmed = actor?.trim();
  if (!trimmed) return null;
  const upper = trimmed.toUpperCase();
  if (upper === 'STAFF' || upper === 'MERCHANT') return 'Staff';
  if (upper === 'CUSTOMER') return 'Customer';
  if (upper === 'SYSTEM' || upper === 'PLATFORM') return 'System';
  return titleCaseEnum(trimmed);
}

export function formatTimelineLabel(
  event: Pick<OrderTimelineEvent, 'label' | 'message' | 'type' | 'status' | 'id'>,
): string {
  const labelled = event.label?.trim() || event.message?.trim();
  if (labelled) {
    const marked = labelled.match(/^Marked\s+([A-Z][A-Z0-9_]+)$/i);
    if (marked?.[1]) return `Marked ${formatOrderStatus(marked[1])}`;
    return labelled;
  }
  if (event.type) return titleCaseEnum(event.type);
  if (event.status) return formatOrderStatus(event.status);
  return event.id?.trim() || 'Update';
}

export function deliveryFromEnvelope(envelope: unknown): DeliveryStatus | undefined {
  const raw =
    readObjectFieldFromEnvelope<DeliveryStatus>(envelope, ['delivery']) ??
    readEntityFromEnvelope<DeliveryStatus>(envelope, ['delivery']);
  if (!raw || typeof raw !== 'object') return undefined;
  const nested = (raw as { delivery?: DeliveryStatus }).delivery;
  const delivery =
    nested && typeof nested === 'object' && (nested.stage != null || nested.state != null)
      ? nested
      : raw;
  if (!('stage' in delivery) && !('state' in delivery) && !('stageLabel' in delivery)) {
    return undefined;
  }
  const stage = delivery.stage ?? delivery.state;
  let etaMinutes = delivery.etaMinutes ?? null;
  if (etaMinutes == null && delivery.etaAt) {
    const ms = Date.parse(delivery.etaAt) - Date.now();
    if (Number.isFinite(ms)) etaMinutes = Math.max(0, Math.round(ms / 60_000));
  }
  return {
    ...delivery,
    state: stage,
    stage,
    stageLabel: delivery.stageLabel ?? stage,
    etaMinutes,
  };
}

/** Stages where merchant "Dispatch now" is no longer the primary action. */
export function isDeliveryAlreadyDispatched(delivery: DeliveryStatus | undefined): boolean {
  const stage = (delivery?.stage ?? delivery?.state ?? '').toUpperCase();
  if (!stage) return false;
  return [
    'COURIER_ASSIGNED',
    'COURIER_ON_WAY',
    'COURIER_AT_SHOP',
    'ON_THE_WAY',
    'ARRIVING',
    'ASSIGNED',
    'AT_PICKUP',
    'PICKED_UP',
    'COLLECTED',
    'EN_ROUTE',
    'AT_DROPOFF',
    'NEAR_DROPOFF',
    'ARRIVED',
    'DELIVERED',
    'COMPLETED',
  ].some((s) => stage === s || stage.includes(s));
}

export function isIncomingOrder(order: { status?: string | null } | undefined): boolean {
  const status = order?.status ?? '';
  return (
    status === 'PENDING_ACCEPTANCE' ||
    status === 'AWAITING_ACCEPTANCE' ||
    status === 'NEW' ||
    status === 'INCOMING'
  );
}

/** 07-05: expired and accepted-elsewhere states must be dismissible. */
export function incomingModalDismissible(
  order: { status?: string | null; claimedByName?: string | null } | undefined,
  expired: boolean,
): boolean {
  if (expired) return true;
  if (order?.claimedByName?.trim()) return true;
  return !isIncomingOrder(order);
}

/** 07-05: scheduled orders show the slot, not the ASM-152 three-minute window. */
export function isScheduledOrder(order: OrderSummary | undefined): boolean {
  return Boolean(order?.isScheduled || order?.scheduledSlot);
}

export function scheduledSlotLabel(order: OrderSummary | undefined): string | null {
  if (!order) return null;
  const slot = order.scheduledSlot;
  if (slot?.label?.trim()) return `Slot ${slot.label.trim()}`;
  const start = slot?.startAt ?? order.promisedAt;
  if (start) {
    const t = Date.parse(start);
    if (Number.isFinite(t)) {
      return `Slot ${new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
  }
  return order.isScheduled ? 'Scheduled' : null;
}

export function lineIsUnavailable(line: OrderLine): boolean {
  if (line.isUnavailable || line.outOfStock || line.unavailable) return true;
  const status = (line.availabilityStatus ?? line.stockStatus ?? '').toUpperCase();
  return status === 'UNAVAILABLE' || status === 'OUT_OF_STOCK' || status === 'SNOOZED';
}

export function isPrepOrder(order: OrderSummary | undefined): boolean {
  const status = order?.status ?? '';
  return status === 'ACCEPTED' || status === 'PREPARING';
}

export function fulfilmentIsCollection(mode?: string | null): boolean {
  const m = (mode ?? '').toUpperCase();
  return m.includes('COLLECTION') || m.includes('PICKUP') || m.includes('COLLECT');
}

export function isDeliveryFulfilment(mode?: string | null): boolean {
  const m = (mode ?? '').toUpperCase();
  return m.includes('DELIVERY') || m.includes('COURIER');
}

/** Collecting: customer expected at the counter (07-07 column design). */
export function isCollectingOrder(order: OrderSummary | undefined): boolean {
  if (!order) return false;
  const status = order.status ?? '';
  if (status === 'COLLECTING' || status === 'AWAITING_COLLECTION') return true;
  if (!fulfilmentIsCollection(order.fulfilmentMode)) return false;
  return status === 'READY';
}

/** Ready: awaiting courier or collection that is not at the counter. */
export function isReadyOrder(order: OrderSummary | undefined): boolean {
  if (!order || isCollectingOrder(order)) return false;
  const status = order.status ?? '';
  return status === 'READY' || status === 'AWAITING_COURIER';
}

export function isOutForDeliveryOrder(order: OrderSummary | undefined): boolean {
  if (!order || isCollectingOrder(order)) return false;
  const status = order.status ?? '';
  return status === 'IN_TRANSIT' || status === 'DELIVERY_FAILED';
}

export function boardHandoverColumnTitle(items: readonly OrderSummary[]): string {
  const hasOut = items.some((order) => isOutForDeliveryOrder(order));
  const hasCollect = items.some((order) => isCollectingOrder(order));
  if (hasOut && hasCollect) return 'Collecting / Out';
  if (hasOut) return 'Out for delivery';
  return 'Collecting';
}

export type BoardAdvanceAction = 'start-prep' | 'mark-ready' | 'handover' | 'courier';

export function nextBoardAdvance(
  order: OrderSummary | undefined,
): { action: BoardAdvanceAction; label: string } | null {
  if (!order) return null;
  if (order.status === 'ACCEPTED') return { action: 'start-prep', label: 'Start prep' };
  if (order.status === 'PREPARING') return { action: 'mark-ready', label: 'Mark ready' };
  if (isCollectingOrder(order)) return { action: 'handover', label: 'Complete handover' };
  if (
    isDeliveryFulfilment(order.fulfilmentMode) &&
    (order.status === 'READY' ||
      order.status === 'AWAITING_COURIER' ||
      isOutForDeliveryOrder(order))
  ) {
    return { action: 'courier', label: 'BeatQ Del' };
  }
  return null;
}

export function isActiveBoardOrder(order: OrderSummary | undefined): boolean {
  const status = order?.status ?? '';
  return !isMerchantHistoryStatus(status);
}

export function isOrderOverdue(order: OrderSummary | undefined, now = Date.now()): boolean {
  if (!order?.promisedAt) return false;
  const t = Date.parse(order.promisedAt);
  return Number.isFinite(t) && t < now && isActiveBoardOrder(order) && !isIncomingOrder(order);
}

/** Minutes past promisedAt for active prep/ready orders; 0 when not overdue. */
export function overdueMinutes(order: OrderSummary | undefined, now = Date.now()): number {
  if (!isOrderOverdue(order, now) || !order?.promisedAt) return 0;
  return Math.max(1, Math.floor((now - Date.parse(order.promisedAt)) / 60000));
}

/** Visual urgency tier 0–3 for overdue prep/ready cards (SCR-MER-050 wireframe). */
export function lateSeverity(order: OrderSummary | undefined, now = Date.now()): number {
  const mins = overdueMinutes(order, now);
  if (mins >= 10) return 3;
  if (mins >= 3) return 2;
  return 0;
}

export function readableOrderDuration(minutes: number): string {
  const wholeMinutes = Math.max(0, Math.floor(minutes));
  if (wholeMinutes < 60) return `${wholeMinutes} min`;
  const hours = Math.floor(wholeMinutes / 60);
  if (hours < 24) return `${hours} hr${wholeMinutes % 60 ? ` ${wholeMinutes % 60} min` : ''}`;
  const days = Math.floor(hours / 24);
  return `${days}d${hours % 24 ? ` ${hours % 24} hr` : ''}`;
}

export function comparePrepColumnOrders(
  a: OrderSummary,
  b: OrderSummary,
  now = Date.now(),
): number {
  const aOver = isOrderOverdue(a, now) ? 1 : 0;
  const bOver = isOrderOverdue(b, now) ? 1 : 0;
  if (aOver !== bOver) return bOver - aOver;
  const sevDiff = lateSeverity(b, now) - lateSeverity(a, now);
  if (sevDiff !== 0) return sevDiff;
  const aT = Date.parse(a.promisedAt ?? '') || Number.MAX_SAFE_INTEGER;
  const bT = Date.parse(b.promisedAt ?? '') || Number.MAX_SAFE_INTEGER;
  return aT - bT;
}

export function sortPrepColumnOrders(orders: OrderSummary[], now = Date.now()): OrderSummary[] {
  return [...orders].sort((a, b) => comparePrepColumnOrders(a, b, now));
}

export function prepCountdownDisplay(order: OrderSummary, now = Date.now()): string {
  if (isOrderOverdue(order, now)) {
    return `${readableOrderDuration(overdueMinutes(order, now))} overdue`;
  }
  return countdownPromisedLabel(order.promisedAt, now);
}

function countdownPromisedLabel(promisedAt?: string | null, now = Date.now()): string {
  if (!promisedAt) return 'No ETA';
  const ms = Date.parse(promisedAt) - now;
  if (!Number.isFinite(ms)) return 'No ETA';
  const mins = Math.round(ms / 60000);
  if (mins < 0) return `${Math.abs(mins)}m overdue`;
  if (mins === 0) return 'Due now';
  return `${mins}m left`;
}

/** Remaining ms until accept deadline; null if none / not incoming. Uses server clock when offset provided. */
export function acceptDeadlineRemainingMs(
  order: OrderSummary | undefined,
  now = Date.now(),
  clockOffsetMs = 0,
): number | null {
  if (!order || !isIncomingOrder(order)) return null;
  const serverNow = now + clockOffsetMs;
  const deadline = order.acceptanceDeadlineAt ?? order.acceptanceDeadline;
  if (deadline) {
    const ms = Date.parse(deadline) - serverNow;
    return Number.isFinite(ms) ? ms : null;
  }
  // Scheduled acceptance is a separate window (07-05); do not invent ASM-152 from placedAt.
  if (isScheduledOrder(order)) return null;
  if (order.placedAt) {
    const start = Date.parse(order.placedAt);
    if (Number.isFinite(start)) return start + ACCEPT_WINDOW_MS - serverNow;
  }
  if (typeof order.secondsToDeadline === 'number' && Number.isFinite(order.secondsToDeadline)) {
    return order.secondsToDeadline * 1000;
  }
  return null;
}

export function acceptCountdownLabel(remainingMs: number | null): string {
  if (remainingMs == null) return '';
  if (remainingMs <= 0) return 'Accept window expired';
  const totalSec = Math.ceil(remainingMs / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m <= 0) return `${s}s to accept`;
  return `${m}:${String(s).padStart(2, '0')} to accept`;
}

/** An incoming status is actionable only while its server accept window remains open. */
export function isActionableIncomingOrder(
  order: OrderSummary | undefined,
  now = Date.now(),
  clockOffsetMs = 0,
): boolean {
  if (!isIncomingOrder(order)) return false;
  const remainingMs = acceptDeadlineRemainingMs(order, now, clockOffsetMs);
  return remainingMs == null || remainingMs > 0;
}

export function isOperationallyLiveOrder(
  order: OrderSummary | undefined,
  now = Date.now(),
  clockOffsetMs = 0,
): boolean {
  if (!isActiveBoardOrder(order)) return false;
  return !isIncomingOrder(order) || isActionableIncomingOrder(order, now, clockOffsetMs);
}

export function formatOrderValue(minor?: number | null, currency = 'GBP'): string | null {
  if (minor == null || !Number.isFinite(minor)) return null;
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(minor / 100);
}

export function sortIncomingColumnOrders(
  orders: OrderSummary[],
  now = Date.now(),
  clockOffsetMs = 0,
): OrderSummary[] {
  return [...orders].sort((a, b) => {
    const aRem = acceptDeadlineRemainingMs(a, now, clockOffsetMs);
    const bRem = acceptDeadlineRemainingMs(b, now, clockOffsetMs);
    const aUrgent = aRem != null && aRem <= ACCEPT_ESCALATE_MS ? 1 : 0;
    const bUrgent = bRem != null && bRem <= ACCEPT_ESCALATE_MS ? 1 : 0;
    if (aUrgent !== bUrgent) return bUrgent - aUrgent;
    const aMs = aRem ?? Number.MAX_SAFE_INTEGER;
    const bMs = bRem ?? Number.MAX_SAFE_INTEGER;
    return aMs - bMs;
  });
}

export function mergePinnedOrders(
  sorted: OrderSummary[],
  pinned: OrderSummary[] | null,
  pinUntil: number,
  now: number,
): OrderSummary[] {
  if (now >= pinUntil || !pinned?.length) return sorted;
  const byId = new Map(sorted.map((order) => [order.id, order]));
  const merged: OrderSummary[] = [];
  for (const order of pinned) {
    const current = order.id ? byId.get(order.id) : undefined;
    if (current) merged.push(current);
  }
  const pinnedIds = new Set(merged.map((order) => order.id));
  for (const order of sorted) {
    if (!order.id || !pinnedIds.has(order.id)) merged.push(order);
  }
  return merged;
}

export function rejectConsequenceCopy(reason: string): string {
  if (reason === 'SUSPECTED_FRAUD') {
    return 'The customer is refunded. This reason goes to platform review and does not count against the branch acceptance rate.';
  }
  return 'The customer is refunded in full. This rejection counts against the branch acceptance rate.';
}

export function cancelConsequenceCopy(): string {
  return 'The customer is refunded in full. This cancellation counts against the branch reliability metric, and the branch remains liable for the order.';
}

export function customerImpactForDelay(
  additionalMinutes: number,
  currentPromise?: string | null,
): string {
  const extra = Number.isFinite(additionalMinutes) ? additionalMinutes : 0;
  let next = '';
  if (currentPromise) {
    const t = Date.parse(currentPromise);
    if (Number.isFinite(t)) {
      next = ` New promised time: ${new Date(t + extra * 60_000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`;
    }
  }
  return `The customer is notified of the new time.${next} Repeated delays on one order raise an operational alert.`;
}

const PROVIDER_NAME_RE = /uber|deliveroo|just\s*eat|stuart|door\s*dash|skip|grab|glovo/i;

/** INV-08: never surface a delivery provider identity. */
export function beatqRiderLabel(courierName?: string | null): string {
  const first = firstNameOnly(courierName);
  if (first && !PROVIDER_NAME_RE.test(first)) return beatqDelIdentity(first);
  return beatqDelIdentity();
}

export type RestockEligibility = 'restock' | 'dispose' | 'licence';

export function restockEligibilityForLine(line: OrderLine): RestockEligibility {
  if (line.isAgeRestricted) return 'licence';
  if (line.isPerishable || line.requiresRefrigeration || line.isChilled) return 'dispose';
  return 'restock';
}

export function restockEligibilityLabel(kind: RestockEligibility): string {
  if (kind === 'dispose') return 'Dispose — perishable, do not restock';
  if (kind === 'licence') return 'Licence conditions — not a platform default';
  return 'Eligible to restock';
}

export function orderAddressVisible(order: OrderDetail | undefined): boolean {
  if (!order || isIncomingOrder(order)) return false;
  return isDeliveryFulfilment(order.fulfilmentMode);
}

export function customerDisplayName(
  order: OrderDetail | undefined,
  afterAcceptance: boolean,
): string {
  const first =
    order?.customerFirstName ||
    order?.customer?.firstName ||
    firstNameOnly(order?.customerName) ||
    firstNameOnly(order?.customer?.fullName);
  const delivery = isDeliveryFulfilment(order?.fulfilmentMode);
  if (afterAcceptance && delivery) {
    const full = order?.customer?.fullName?.trim();
    if (full) return full;
  }
  return first || 'Customer';
}

export function formatAllergenLabel(code?: string | null): string {
  if (!code) return '';
  return titleCaseEnum(code);
}

export function settlementLooksUnrecorded(order: OrderDetail | undefined): boolean {
  const settlement = order?.merchantSettlement;
  if (!settlement) return false;
  return (
    (settlement.merchantEntitlement?.minor ?? 0) === 0 &&
    (settlement.commission?.minor ?? 0) === 0 &&
    settlement.commissionRateBps == null &&
    (settlement.originalMerchandiseSubtotal?.minor ?? 0) > 0
  );
}

export function deliveryAddressText(order: OrderDetail | undefined): string | null {
  if (!orderAddressVisible(order)) return null;
  const raw = order?.customer?.deliveryAddress ?? order?.deliveryAddress ?? order?.address;
  if (!raw) return null;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (typeof raw === 'object') {
    const rec = raw as Record<string, unknown>;
    const parts = ['line1', 'line2', 'city', 'postcode', 'formatted']
      .map((k) => (typeof rec[k] === 'string' ? (rec[k] as string) : ''))
      .filter(Boolean);
    if (parts.length) return parts.join(', ');
  }
  return null;
}

/** Dev/default: auto-open print after accept unless explicitly disabled. */
export function isAutoPrintOnAcceptEnabled(): boolean {
  try {
    const v = localStorage.getItem('beatq.merchant.autoPrintOnAccept');
    if (v === '0' || v === 'false') return false;
    return true;
  } catch {
    return true;
  }
}

export function shortOrderLabel(order: OrderSummary | undefined): string {
  const ref = order?.publicReference?.trim();
  if (ref) {
    const digits = ref.replace(/\D/g, '');
    if (digits.length >= 3) return `#${digits.slice(-4)}`;
    return ref.length > 8 ? `#${ref.slice(-4)}` : ref;
  }
  const id = order?.id ?? '';
  return id ? `#${id.slice(-4).toUpperCase()}` : 'Order';
}

export const BOARD_COLUMN_TITLES = {
  incoming: 'New',
  prep: 'Preparing',
  ready: 'Ready',
  collecting: 'Collecting',
} as const;

export function boardColumnAccessibleName(title: string, count: number): string {
  return `${title} (${count})`;
}

export function orderCardAccessibleName(
  reference: string,
  columnTitle: string,
  timeFact: string,
): string {
  return `${reference}, ${columnTitle}, ${timeFact}`;
}

/** First paint is silent; later ids are announced as new arrivals (07-10). */
export function newIncomingArrivalIds(
  previousIds: readonly string[] | null,
  nextIds: readonly string[],
): string[] {
  if (previousIds == null) return [];
  return nextIds.filter((id) => !previousIds.includes(id));
}

export function newOrderArrivalAnnouncement(label: string): string {
  return `New order. ${label}. Three minutes to accept`;
}

export function firstUnclaimedIncomingId(orders: readonly OrderSummary[]): string | undefined {
  return orders.find((order) => Boolean(order.id) && !order.claimedByName)?.id;
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!target || typeof target !== 'object') return false;
  const el = target as { tagName?: string; isContentEditable?: boolean };
  const tag = (el.tagName ?? '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return Boolean(el.isContentEditable);
}

export function isBoardAcceptShortcut(event: {
  altKey: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  key: string;
  code?: string;
}): boolean {
  if (!event.altKey || event.ctrlKey || event.metaKey) return false;
  return event.key === 'a' || event.key === 'A' || event.code === 'KeyA';
}

export async function getOrderBoard(params?: Record<string, string>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/orders/board', {
    params: { query: params as never },
    headers: merchantScopeHeaders(),
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listOrders(params?: Record<string, string>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/orders', {
    params: { query: params as never },
    headers: merchantScopeHeaders(),
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getOrder(orderId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/orders/{id}', {
    params: { path: { id: orderId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getOrderTimeline(orderId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/orders/{id}/timeline', {
    params: { path: { id: orderId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function acceptOrder(orderId: string, body: Record<string, unknown> = {}) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/accept', {
    params: { path: { id: orderId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function rejectOrder(
  orderId: string,
  body: { reason: string; comment?: string; notes?: string },
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/reject', {
    params: { path: { id: orderId } },
    body: {
      reason: body.reason,
      ...(body.comment?.trim() || body.notes?.trim()
        ? { comment: (body.comment ?? body.notes)!.trim() }
        : {}),
    } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function cancelOrder(
  orderId: string,
  body: {
    reason: string;
    comment?: string;
    notes?: string;
    acknowledgeImpact?: boolean;
  },
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/cancel', {
    params: { path: { id: orderId } },
    body: {
      reason: body.reason,
      ...(body.comment?.trim() || body.notes?.trim()
        ? { comment: (body.comment ?? body.notes)!.trim() }
        : {}),
      acknowledgeImpact: body.acknowledgeImpact !== false,
    } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function adjustPromisedTime(
  orderId: string,
  body: { additionalMinutes: number; reason?: string; notifyCustomer?: boolean },
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/adjust-prep-time', {
    params: { path: { id: orderId } },
    body: {
      additionalMinutes: body.additionalMinutes,
      ...(body.reason ? { reason: body.reason } : {}),
      notifyCustomer: body.notifyCustomer !== false,
    } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getOrderDelivery(orderId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/orders/{id}/delivery', {
    params: { path: { id: orderId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function reprintTicket(orderId: string, body: Record<string, unknown> = {}) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/ticket/reprint', {
    params: { path: { id: orderId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

async function postMerchantOrderPath(path: string, body: Record<string, unknown> = {}) {
  // TODO(delivery-sdk): replace this decoder when handover/return command paths are generated.
  const token = getAccessToken();
  const res = await fetch(`${getApiBaseUrl().replace(/\/$/, '')}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...csrfHeaders(),
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(sdkErrorMessage(json));
  return json;
}

export async function markOrderPreparing(orderId: string) {
  return postMerchantOrderPath(`/merchant/v1/orders/${encodeURIComponent(orderId)}/preparing`);
}

export async function markOrderReady(orderId: string) {
  return postMerchantOrderPath(`/merchant/v1/orders/${encodeURIComponent(orderId)}/ready`);
}

export async function confirmReturnedReceipt(orderId: string, body: Record<string, unknown> = {}) {
  return postMerchantOrderPath(
    `/merchant/v1/orders/${encodeURIComponent(orderId)}/returned/confirm-receipt`,
    { acknowledgeNoLiability: true, ...body },
  );
}

export async function completeOrderHandover(orderId: string, body: Record<string, unknown> = {}) {
  const response = await postMerchantOrderPath(
    `/merchant/v1/orders/${encodeURIComponent(orderId)}/handover`,
    body,
  );
  const root = response as {
    data?: { order?: { status?: string }; status?: string };
    order?: { status?: string };
    status?: string;
  };
  const status =
    root.data?.order?.status ?? root.data?.status ?? root.order?.status ?? root.status ?? null;
  if (!status) {
    throw new Error('Handover was not confirmed by the server. Refresh the order before retrying.');
  }
  return response;
}

export async function addOrderNote(orderId: string, note: string) {
  return postMerchantOrderPath(`/merchant/v1/orders/${encodeURIComponent(orderId)}/notes`, {
    note,
  });
}

function deliveryCommandIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function dispatchDeliveryNow(orderId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/orders/{id}/delivery/dispatch-now',
    {
      params: { path: { id: orderId } },
      headers: { 'Idempotency-Key': deliveryCommandIdempotencyKey() },
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function reportDeliveryIssue(
  orderId: string,
  body: { issueCode: string; note?: string; issueType?: string; description?: string },
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/orders/{id}/delivery/report-issue',
    {
      params: { path: { id: orderId } },
      headers: { 'Idempotency-Key': deliveryCommandIdempotencyKey() },
      body: {
        issueCode: body.issueCode,
        ...(body.note ? { note: body.note } : {}),
      },
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  const issue = data.data;
  if (!issue.id || !issue.status) {
    throw new Error('The issue was not confirmed by support. Keep this note and try again.');
  }
  return { ...issue, id: issue.id, status: issue.status };
}

/** 10-05a PATCH /branches/{id}/capacity — prepMinutes + BUSY, from the board. */
export async function extendBranchPrepMinutes(branchId: string, prepMinutes: number) {
  const client = getMerchantClient();
  const { data, error, response } = await client.PATCH('/merchant/v1/branches/{id}/capacity', {
    params: { path: { id: branchId } },
    body: { prepMinutes, capacityState: 'BUSY' } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function exportOrderHistory(params?: { status?: string; branchId?: string }) {
  const payload = buildOrderHistoryExportPayload(params);
  // Reporting exports return raw 202 `{ runId, state }` (not ok() envelope).
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/reports/exports', {
    body: payload as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data ?? {};
}

export type ReplacementRequest = {
  id?: string;
  orderId?: string;
  status?: string;
  reason?: string | null;
  notes?: string | null;
  requestedAt?: string;
};

export function replacementsFromEnvelope(envelope: unknown): ReplacementRequest[] {
  return readListFromEnvelope<ReplacementRequest>(envelope, ['replacements']);
}

export async function listOrderReplacements(orderId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET(
    '/merchant/v1/orders/{id}/replacement' as never,
    {
      params: { path: { id: orderId } },
    },
  );
  if (isOptionalReadMiss(response.status)) return emptyListPayload('replacements');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function createOrderReplacement(
  orderId: string,
  body: { reason?: string; notes?: string } = {},
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/replacement', {
    params: { path: { id: orderId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}
